import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, X, ImageIcon, FileText, Crop, Loader2 } from "lucide-react";
import { ImageCropDialog } from "@/components/ImageCropDialog";

const MAX_IMAGE = 5 * 1024 * 1024;
const MAX_DOC = 10 * 1024 * 1024;

const uploadToIntake = async (token, kind, file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/public/intake/${token}/upload`, form, {
    params: { kind },
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

/** Public-form headshot: choose a file, frame it square, upload. */
export function JoinHeadshotUpload({ token, value, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [pending, setPending] = useState(null);

  const send = async (file) => {
    setBusy(true);
    try {
      const data = await uploadToIntake(token, "headshot", file);
      onChange(data.ref);
      setPreview(URL.createObjectURL(file));
      toast.success("Headshot attached");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload that photo");
    } finally { setBusy(false); }
  };

  const pick = (file) => {
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > MAX_IMAGE) { toast.error("That photo is over 5 MB — try a smaller one"); return; }
    setPending({ src: URL.createObjectURL(file), name: file.name });
  };

  const closeCrop = () => { setPending(null); };

  return (
    <div className="space-y-2" data-testid="join-headshot-field">
      <Label className="text-xs text-white/70">Headshot / profile photo</Label>
      <div className="flex items-start gap-4">
        <div data-testid="join-headshot-preview"
             className="h-20 w-20 shrink-0 overflow-hidden rounded-full border border-dashed border-white/20 bg-white/[0.04] grid place-items-center">
          {preview
            ? <img src={preview} alt="Headshot" className="h-full w-full object-cover" />
            : <ImageIcon className="h-5 w-5 text-white/25" />}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <input ref={fileRef} type="file" className="hidden" data-testid="join-headshot-input"
                   accept="image/png,image/jpeg,image/webp"
                   onChange={(e) => pick(e.target.files?.[0])} />
            <Button type="button" size="sm" variant="secondary" disabled={busy}
                    data-testid="join-headshot-upload" onClick={() => fileRef.current?.click()}>
              {busy ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Uploading…</>
                     : <><Upload className="mr-1.5 h-3.5 w-3.5" /> {value ? "Replace photo" : "Upload photo"}</>}
            </Button>
            {value && preview && (
              <Button type="button" size="sm" variant="ghost" className="text-white/70"
                      data-testid="join-headshot-recrop"
                      onClick={() => setPending({ src: preview, name: "headshot.jpg" })}>
                <Crop className="mr-1.5 h-3.5 w-3.5" /> Re-crop
              </Button>
            )}
            {value && (
              <Button type="button" size="sm" variant="ghost" className="text-white/70"
                      data-testid="join-headshot-clear"
                      onClick={() => { onChange(""); setPreview(null); }}>
                <X className="mr-1 h-3.5 w-3.5" /> Remove
              </Button>
            )}
          </div>
          <p className="text-[11px] text-white/40">
            JPG, PNG or WebP up to 5 MB. You'll get to frame it before it's sent.
          </p>
        </div>
      </div>

      <ImageCropDialog open={Boolean(pending)} src={pending?.src} aspect={1} round
                       filename={pending?.name || "headshot.jpg"} testId="join-headshot-crop"
                       onCancel={closeCrop}
                       onCropped={(file) => { closeCrop(); send(file); }} />
    </div>
  );
}

/** Public-form document upload (resume / CV). */
export function JoinFileUpload({ token, value, onChange, label = "Resume / CV", kind = "resume" }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");

  const pick = async (file) => {
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (file.size > MAX_DOC) { toast.error("That file is over 10 MB — try a smaller one"); return; }
    setBusy(true);
    try {
      const data = await uploadToIntake(token, kind, file);
      onChange(data.ref);
      setName(data.filename || file.name);
      toast.success("File attached");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload that file");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid={`join-${kind}-field`}>
      <Label className="text-xs text-white/70">{label}</Label>
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" className="hidden" data-testid={`join-${kind}-input`}
               accept=".pdf,.doc,.docx,.rtf,.txt,application/pdf"
               onChange={(e) => pick(e.target.files?.[0])} />
        <Button type="button" size="sm" variant="secondary" disabled={busy}
                data-testid={`join-${kind}-upload`} onClick={() => fileRef.current?.click()}>
          {busy ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Uploading…</>
                 : <><FileText className="mr-1.5 h-3.5 w-3.5" /> {value ? "Replace file" : "Upload file"}</>}
        </Button>
        {value && name && <span className="text-xs text-white/60" data-testid={`join-${kind}-name`}>{name}</span>}
        {value && (
          <Button type="button" size="sm" variant="ghost" className="text-white/70"
                  data-testid={`join-${kind}-clear`}
                  onClick={() => { onChange(""); setName(""); }}>
            <X className="mr-1 h-3.5 w-3.5" /> Remove
          </Button>
        )}
      </div>
      <p className="text-[11px] text-white/40">PDF, DOC, DOCX, RTF or TXT up to 10 MB.</p>
    </div>
  );
}
